import { payFromAccount, type AccountLike } from '../budget/accounts';
import { singletonId } from '../ids';
import type { Mutation } from '../mutations';
import type { BudgetSettings } from '../rules/readModel';
import type { BuildCtx } from '../spending/types';
import { paymentsMutation } from './types';

/**
 * v0.7.4 Payments settings (Ivan, 2026-11-04): one synced row per user. "Pay from by default" is
 * the account a payment leaves when the payee has no account of its own; "Remind me … days
 * before" is the starting value of a new payee's remind days.
 */
export interface PaymentsSettings {
  defaultAccountId: string | null;
  remindDays: number;
}

export const DEFAULT_PAYMENTS_SETTINGS: PaymentsSettings = {
  defaultAccountId: null,
  remindDays: 3,
};
export const MAX_REMIND_DAYS = 30;

export function paymentsSettingsId(userId: string): string {
  return singletonId(userId, 'payments_settings');
}

export type PaymentsSettingsError = 'bad_remind_days';

export const isValidRemindDays = (n: number): boolean =>
  Number.isInteger(n) && n >= 0 && n <= MAX_REMIND_DAYS;

/**
 * A partial upsert (conventions §3): an insert carrying only the fields being set (every other
 * column has a default or is nullable), so a device that hasn't pulled the row yet never
 * overwrites a field it didn't touch.
 */
export function buildSetPaymentsSettings(
  userId: string,
  patch: Partial<PaymentsSettings>,
  ctx: BuildCtx,
): { ok: true; mutation: Mutation | null } | { ok: false; error: PaymentsSettingsError } {
  const values: Record<string, unknown> = {};
  if (patch.defaultAccountId !== undefined) values.defaultAccountId = patch.defaultAccountId;
  if (patch.remindDays !== undefined) {
    if (!isValidRemindDays(patch.remindDays)) return { ok: false, error: 'bad_remind_days' };
    values.remindDays = patch.remindDays;
  }
  if (Object.keys(values).length === 0) return { ok: true, mutation: null };
  return {
    ok: true,
    mutation: paymentsMutation(
      ctx,
      'payments_settings',
      'insert',
      paymentsSettingsId(userId),
      values,
    ),
  };
}

/**
 * The account a payment leaves: picked on the pay sheet, else the payee's own, else Payments'
 * "Pay from by default" (while it's a live account used for spending), else Budget's "Pay from".
 */
export function paymentPayFrom(
  picked: string | null | undefined,
  payeeAccountId: string | null | undefined,
  payments: Pick<PaymentsSettings, 'defaultAccountId'>,
  budget: Pick<BudgetSettings, 'useFunds' | 'defaultAccountId'>,
  budgetUsable: boolean,
  accounts?: readonly Pick<AccountLike, 'id' | 'deletedAt' | 'useForSpending'>[],
): string | null {
  if (!budgetUsable) return null;
  const own = picked ?? payeeAccountId ?? null;
  if (own) return payFromAccount(own, null, budget, true, accounts);
  return (
    payFromAccount(
      null,
      null,
      { useFunds: false, defaultAccountId: payments.defaultAccountId },
      true,
      accounts,
    ) ?? payFromAccount(null, null, budget, true, accounts)
  );
}
